import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { MarketingContent, MarketingContentPatch, MarketingContentType } from '../types/marketing';
import {
  TEMPLATES,
  getTemplateOrDefault,
  WorkflowTemplate,
  PODCAST_VIDEO_TEMPLATE,
} from '../utils/marketingTemplates';
import {
  subscribeToMarketingContent,
  createContent,
  updateContent,
  setStageStatus,
  updateStageNote,
  deleteContent,
} from '../utils/marketingService';
import MarketingSidebar from '../components/marketing/MarketingSidebar';
import MarketingKanban from '../components/marketing/MarketingKanban';
import MarketingTable from '../components/marketing/MarketingTable';
import ContentDetailPanel from '../components/marketing/ContentDetailPanel';
import NewContentModal from '../components/marketing/NewContentModal';

type ViewMode = 'board' | 'table';

function lastStageId(templateId: string): string {
  const t = getTemplateOrDefault(templateId);
  return t.stages[t.stages.length - 1]?.id ?? '';
}

const MarketingContentPipelinePage: React.FC = () => {
  const { user } = useAuth();

  const [items, setItems] = useState<MarketingContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [selectedType, setSelectedType] = useState<MarketingContentType | null>(null);
  const [selectedItem, setSelectedItem] = useState<MarketingContent | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [hideArchived, setHideArchived] = useState(true);

  useEffect(() => {
    const unsub = subscribeToMarketingContent(rows => {
      setItems(rows);
      setLoading(false);
    });
    return unsub;
  }, []);

  const activeTemplate: WorkflowTemplate = useMemo(() => {
    if (selectedType) {
      const match = Object.values(TEMPLATES).find(t => t.contentType === selectedType);
      if (match) return match;
    }
    return PODCAST_VIDEO_TEMPLATE;
  }, [selectedType]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (selectedType) {
      result = result.filter(i => i.contentType === selectedType);
    }
    if (hideArchived) {
      result = result.filter(i => i.stageStatuses?.[lastStageId(i.templateId)]?.status !== 'done');
    }
    return result;
  }, [items, selectedType, hideArchived]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: items.length };
    for (const i of items) {
      counts[i.contentType] = (counts[i.contentType] || 0) + 1;
    }
    return counts;
  }, [items]);

  const stats = useMemo(() => {
    const total = filteredItems.length;
    let inProgress = 0;
    let published = 0;
    let archived = 0;
    for (const i of items) {
      const lastId = lastStageId(i.templateId);
      const isArchived = i.stageStatuses?.[lastId]?.status === 'done';
      const publishStage = i.stageStatuses?.['publish']?.status === 'done';
      if (isArchived) archived++;
      else if (publishStage) published++;
      else inProgress++;
    }
    return { total, inProgress, published, archived };
  }, [filteredItems, items]);

  const handleCreate = useCallback(
    async (input: { title: string; template: WorkflowTemplate; recordingDate?: Date | null; guestName?: string | null }) => {
      if (!user) return;
      await createContent({
        title: input.title,
        template: input.template,
        recordingDate: input.recordingDate ?? null,
        guestName: input.guestName ?? null,
        createdBy: user.id,
      });
      setShowCreate(false);
    },
    [user],
  );

  const handleUpdate = useCallback(
    async (id: string, patch: MarketingContentPatch) => {
      await updateContent(id, patch);
      if (selectedItem?.id === id) {
        setSelectedItem(prev => (prev ? ({ ...prev, ...patch } as MarketingContent) : null));
      }
    },
    [selectedItem],
  );

  const handleStageToggle = useCallback(
    async (item: MarketingContent, stageId: string, done: boolean) => {
      if (!user) return;
      await setStageStatus({
        contentId: item.id,
        stageId,
        state: done ? 'done' : 'todo',
        userId: user.id,
        current: item.stageStatuses,
        templateId: item.templateId,
      });
    },
    [user],
  );

  const handleStageNote = useCallback(
    async (item: MarketingContent, stageId: string, note: string) => {
      await updateStageNote(item.id, stageId, item.stageStatuses, note);
    },
    [],
  );

  const handleDropToStage = useCallback(
    async (item: MarketingContent, targetStageId: string) => {
      if (!user) return;
      const template = getTemplateOrDefault(item.templateId);
      const targetIdx = template.stages.findIndex(s => s.id === targetStageId);
      if (targetIdx < 0) return;

      const updated = { ...item.stageStatuses };
      template.stages.forEach((s, i) => {
        const cur = updated[s.id]?.status;
        if (i < targetIdx && cur !== 'done' && cur !== 'skipped') {
          updated[s.id] = {
            status: 'done',
            completedAt: null,
            completedBy: user.id,
            note: updated[s.id]?.note ?? null,
          };
        }
        if (i === targetIdx && cur !== 'done') {
          updated[s.id] = {
            ...(updated[s.id] || { status: 'todo' }),
            status: 'in-progress',
          };
        }
      });

      await updateContent(item.id, {
        stageStatuses: updated,
        currentStageId: targetStageId,
      });
    },
    [user],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteContent(id);
      if (selectedItem?.id === id) setSelectedItem(null);
    },
    [selectedItem],
  );

  const freshSelected = useMemo(() => {
    if (!selectedItem) return null;
    return items.find(i => i.id === selectedItem.id) ?? selectedItem;
  }, [selectedItem, items]);

  if (loading) {
    return (
      <div className="marketing-hub-loading">
        <div className="marketing-hub-loading-bar" />
      </div>
    );
  }

  return (
    <>
      <div className="marketing-hub-layout marketing-hub-layout--subpage">
        <MarketingSidebar
          selectedType={selectedType}
          onSelectType={setSelectedType}
          typeCounts={typeCounts}
          hideArchived={hideArchived}
          onToggleArchived={() => setHideArchived(h => !h)}
        />

        <div className="marketing-hub-main">
          <div className="marketing-hub-toolbar">
            <div className="marketing-hub-toolbar-left">
              <h2 className="marketing-hub-title">
                {selectedType ? `${activeTemplate.name}s` : 'All Content'}
              </h2>
              <div className="marketing-hub-stats">
                <span className="mh-stat">{stats.total} shown</span>
                {stats.inProgress > 0 && (
                  <span className="mh-stat mh-stat-active">{stats.inProgress} in&nbsp;progress</span>
                )}
                {stats.published > 0 && (
                  <span className="mh-stat mh-stat-published">{stats.published} published</span>
                )}
                {stats.archived > 0 && (
                  <span className="mh-stat mh-stat-done">{stats.archived} archived</span>
                )}
              </div>
            </div>
            <div className="marketing-hub-toolbar-right">
              <div className="marketing-hub-views" role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`mh-view-btn ${viewMode === 'board' ? 'active' : ''}`}
                  aria-pressed={viewMode === 'board'}
                  onClick={() => setViewMode('board')}
                >
                  Board
                </button>
                <button
                  type="button"
                  className={`mh-view-btn ${viewMode === 'table' ? 'active' : ''}`}
                  aria-pressed={viewMode === 'table'}
                  onClick={() => setViewMode('table')}
                >
                  Table
                </button>
              </div>
              <button type="button" className="mh-new-btn" onClick={() => setShowCreate(true)}>
                <Plus size={14} strokeWidth={2.5} />
                New Content
              </button>
            </div>
          </div>

          <div className="marketing-hub-content">
            <div className={`mh-view-pane ${viewMode === 'board' ? 'active' : ''}`}>
              <MarketingKanban
                items={filteredItems}
                template={activeTemplate}
                onSelect={setSelectedItem}
                onDropToStage={handleDropToStage}
              />
            </div>
            <div className={`mh-view-pane ${viewMode === 'table' ? 'active' : ''}`}>
              <MarketingTable
                items={filteredItems}
                template={activeTemplate}
                onSelect={setSelectedItem}
              />
            </div>
          </div>
        </div>
      </div>

      {freshSelected && (
        <ContentDetailPanel
          content={freshSelected}
          template={getTemplateOrDefault(freshSelected.templateId)}
          currentUserId={user?.id || ''}
          onUpdate={handleUpdate}
          onStageToggle={handleStageToggle}
          onStageNote={handleStageNote}
          onDelete={handleDelete}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {showCreate && (
        <NewContentModal onSubmit={handleCreate} onClose={() => setShowCreate(false)} />
      )}
    </>
  );
};

export default MarketingContentPipelinePage;
